import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DataTable } from '@/components/ui/DataTable'
import { TablePageContainer } from '@/components/ui/DataTable/PageTableContainer'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { getPlatformContainersColumns } from './PlatformContainersTable/platformContainersTableColumns'
import { containerLogsColumns } from './PlatformContainerLogsTable/platformContainerLogsTableColumns'
import { Button } from '@/components/shadcn/button'
import { SpinnerIosRegular } from '@fluentui/react-icons'
import {
  defaultLogTimeFilters,
  LogTimeFilterPopover,
  LogTimeFilters,
} from '@/pages/shared-components/ContainerLogs/containerLogFilters'

type PodStatusResult = RestSchemas['Services.Support.PodStatusResult']
type ContainerLogResult = RestSchemas['Services.Support.ContainerLogResult']

export function PlatformContainersPage() {
  const { getSupportApiQueryCurrentPods, getSupportApiQueryContainerLogs } = useRestClient()

  const { data: pods } = getSupportApiQueryCurrentPods.useSuspenseQuery(undefined)

  const [selectedAppName, setSelectedAppName] = useState<string | null>(null)
  const [isRefreshingPods, setIsRefreshingPods] = useState(false)
  const [isRefreshingLogs, setIsRefreshingLogs] = useState(false)
  const [hasLoadedLogsOnce, setHasLoadedLogsOnce] = useState(false)
  const [logTimeFilters, setLogTimeFilters] = useState<LogTimeFilters>(defaultLogTimeFilters)
  const logsHeaderRef = useRef<HTMLDivElement | null>(null)
  const prevLogsPendingRef = useRef<boolean>(false)

  const { data: logs = [], isFetching: logsPending } = getSupportApiQueryContainerLogs.useQuery(
    selectedAppName
      ? { query: { appName: selectedAppName, ...logTimeFilters } }
      : (undefined as unknown as { query: { appName: string } }),
    { enabled: !!selectedAppName }
  )

  const onViewLogs = useCallback((appName: string) => {
    setSelectedAppName(appName)
  }, [])

  const podsColumns = useMemo(
    () => getPlatformContainersColumns(onViewLogs, logsPending, selectedAppName),
    [onViewLogs, logsPending, selectedAppName]
  )

  const onRefreshPods = useCallback(async () => {
    try {
      setIsRefreshingPods(true)
      await getSupportApiQueryCurrentPods.invalidateQueries()
    } finally {
      setTimeout(() => setIsRefreshingPods(false), 300)
    }
  }, [getSupportApiQueryCurrentPods])

  const onRefreshLogs = useCallback(async () => {
    if (!selectedAppName) return
    const refreshedEndTime = new Date().toISOString()
    const updatedFilters = { ...logTimeFilters, endTime: refreshedEndTime }
    setLogTimeFilters(updatedFilters)
    try {
      setIsRefreshingLogs(true)
      await getSupportApiQueryContainerLogs.invalidateQueries({
        parameters: { query: { appName: selectedAppName, ...updatedFilters } },
      })
    } finally {
      setTimeout(() => setIsRefreshingLogs(false), 300)
    }
  }, [getSupportApiQueryContainerLogs, selectedAppName, logTimeFilters])

  // Reset first-load flag and time filters when switching app
  useEffect(() => {
    setHasLoadedLogsOnce(false)
    setLogTimeFilters(defaultLogTimeFilters())
  }, [selectedAppName])

  // When logs finish loading (pending -> not pending), set first-load flag and scroll into view
  useEffect(() => {
    if (prevLogsPendingRef.current && !logsPending) {
      setHasLoadedLogsOnce(true)
      logsHeaderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    prevLogsPendingRef.current = logsPending
  }, [logsPending])

  return (
    <TablePageContainer fullHeight>
      <div className="px-5">
        <DataTable<PodStatusResult, unknown>
          title="Containers"
          action={
            <Button size="sm" variant="outline" onClick={onRefreshPods} disabled={isRefreshingPods}>
              {isRefreshingPods ? (
                <>
                  <SpinnerIosRegular className="animate-spin" />
                  Refreshing…
                </>
              ) : (
                'Refresh'
              )}
            </Button>
          }
          initialGlobalFilter="apps-"
          defaultSortColumnId="appName"
          columns={podsColumns}
          data={pods as PodStatusResult[]}
        />

        {selectedAppName && (
          <div className="mt-6">
            <div ref={logsHeaderRef} />
            {!hasLoadedLogsOnce && logsPending && (
              <div className="flex items-center gap-2 px-1 py-2 text-muted-foreground">
                <SpinnerIosRegular className="animate-spin" />
                <span>Loading logs…</span>
              </div>
            )}
            <DataTable<ContainerLogResult, unknown>
              title={`Container Logs - ${selectedAppName}`}
              action={
                <div className="flex items-center gap-2">
                  <LogTimeFilterPopover filters={logTimeFilters} onChange={setLogTimeFilters} />
                  <Button size="sm" variant="outline" onClick={onRefreshLogs} disabled={isRefreshingLogs}>
                    {isRefreshingLogs ? (
                      <>
                        <SpinnerIosRegular className="animate-spin" />
                        Refreshing…
                      </>
                    ) : (
                      'Refresh'
                    )}
                  </Button>
                </div>
              }
              columns={containerLogsColumns}
              data={(logs as ContainerLogResult[]) || []}
            />
          </div>
        )}
      </div>
    </TablePageContainer>
  )
}
